import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, Share2, Phone, Info } from "lucide-react";
import html2canvas from "html2canvas";

export default function WalletPass() {
  const { memberNumber } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [howto, setHowto] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get(`/wallet/${memberNumber}`).then(({ data }) => setData(data)).catch(() => setError("Targeta no trobada"));
  }, [memberNumber]);

  const downloadPng = async () => {
    if (!cardRef.current) return;
    const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 3 });
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a"); a.href = url; a.download = `targeta-${memberNumber}.png`; a.click();
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Targeta soci ${memberNumber}`, url: window.location.href }); return; } catch {}
    }
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Enllaç copiat");
  };
  const downloadVCard = () => {
    window.location.href = `${API_BASE}/wallet/${memberNumber}/vcard`;
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="wallet-error">
      <div className="text-center">
        <div className="font-heading font-bold text-xl">Targeta no trobada</div>
        <p className="text-muted-foreground text-sm mt-2">L'associat no existeix o està inactiu.</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregant…</div>;

  const walletUrl = window.location.href;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex flex-col items-center px-4 py-8" data-testid="wallet-page">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <div className="overline text-accent">Targeta digital</div>
          <h1 className="mt-2 font-heading font-bold text-2xl">Shopping Salou</h1>
        </div>

        <div ref={cardRef} className="rounded-3xl overflow-hidden shadow-2xl shadow-primary/30 bg-primary text-primary-foreground p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-accent font-semibold">Shopping Salou</div>
              <div className="text-xs opacity-75 mt-1">Associació de Comerciants</div>
            </div>
            <img src="/logo.jpg" alt="Shopping Salou" className="w-14 h-14 rounded-2xl bg-white/95 p-1 object-contain" />
          </div>
          <div className="text-2xl font-heading font-bold leading-tight">{data.commercial_name}</div>
          <div className="text-sm opacity-80 mt-1 capitalize">{data.category}</div>
          <div className="mt-6 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Número de soci</div>
              <div className="font-mono text-xl font-bold mt-0.5">{data.member_number}</div>
              {data.issue_date && <div className="text-[10px] opacity-70 mt-2">Alta · {new Date(data.issue_date).toLocaleDateString()}</div>}
            </div>
            <div className="bg-white p-2 rounded-xl shrink-0">
              <QRCodeCanvas value={walletUrl} size={110} bgColor="#ffffff" fgColor="#CF0283" level="M" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button onClick={downloadPng} className="rounded-full h-11" data-testid="wallet-download-btn"><Download className="w-4 h-4 mr-2" /> Guardar</Button>
          <Button onClick={share} variant="outline" className="rounded-full h-11" data-testid="wallet-share-btn"><Share2 className="w-4 h-4 mr-2" /> Compartir</Button>
          <Button onClick={downloadVCard} variant="outline" className="rounded-full h-11" data-testid="wallet-vcard-btn"><Phone className="w-4 h-4 mr-2" /> vCard</Button>
          <Button onClick={() => setHowto(!howto)} variant="ghost" className="rounded-full h-11" data-testid="wallet-howto-btn"><Info className="w-4 h-4 mr-2" /> Afegir al mòbil</Button>
        </div>

        {howto && (
          <Card className="rounded-2xl border-2" data-testid="wallet-howto">
            <CardContent className="p-5 space-y-3 text-sm">
              <div>
                <div className="font-semibold">iOS · Safari</div>
                <p className="text-muted-foreground text-xs">Botó Compartir → "Afegir a pantalla d'inici". Es guarda com a app.</p>
              </div>
              <div>
                <div className="font-semibold">Android · Chrome</div>
                <p className="text-muted-foreground text-xs">Menú ⋮ → "Instal·lar aplicació" o "Afegir a pantalla d'inici".</p>
              </div>
              <div className="text-xs text-muted-foreground pt-2 border-t">També pots guardar la imatge al rodet o afegir el contacte al mòbil amb el botó vCard.</div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
